import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { TipoLancamento } from '@models/tipo-lancamento';
import { LoteLancamentos } from '../../models/lote-lancamentos';
import { LancamentoDoLote } from '../../models/lancamento-do-lote';
import { MessageService } from '@services/message.service';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';
import { EditarLoteLancamentosApiService } from '../../services/cadastro-lote-lancamentos-api.service';
import { CardLoteLancamentosComponent } from '../card-lote-lancamentos/card-lote-lancamentos.component';
import { Permissao } from '@models/enums/permissao';

@Component({
  selector: 'app-cadastro-lote',
  standalone: false,
  templateUrl: './cadastro-lote.component.html',
  styleUrl: './cadastro-lote.component.scss'
})
export class CadastroLoteComponent implements OnInit {

  protected get permissaoEditarLancamento() { return Permissao.EDITAR_LANCAMENTO_CAIXA; }

  @Input() tiposLancamento: TipoLancamento[] = [];

  @Output() loteEncontrado = new EventEmitter<LoteLancamentos>();

  modoEdicao: boolean = false;
  loteId: number = 0;
  tabCruzado: boolean = true;

  form: FormGroup;

  get creditosForm(): FormGroup {
    return this.form.get('creditos') as FormGroup;
  }
  get debitosForm(): FormGroup {
    return this.form.get('debitos') as FormGroup;
  }

  get diferenca(): number {
    const somarRows = (form: FormGroup): number => {
      const rows = form.get('rows') as FormArray | null;
      if (!rows) return 0;
      return rows.controls.reduce((acc, row) => acc + (Number(row.get('valor')?.value) || 0), 0);
    };
    return somarRows(this.creditosForm) - somarRows(this.debitosForm);
  }

  @ViewChild("cardCreditos")
  cardCreditos: CardLoteLancamentosComponent;
  @ViewChild("cardDebitos")
  cardDebitos: CardLoteLancamentosComponent;

  constructor(
    private fb: FormBuilder,
    private editarLoteLancamentosApiService: EditarLoteLancamentosApiService,
    private messageService: MessageService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      nomeLote: ["", Validators.required],
      dataLote: [null, Validators.required],
      creditos: this.fb.group({}),
      debitos: this.fb.group({})
    });
  }

  public setDataInicial(data: Date): void {
    this.form.get("dataLote")!.setValue(data);
  }

  public preencherValorCorrespondente(valor: number): void {
    const valorAbsoluto = Math.abs(valor);
    this.cardCreditos.definirValorPrimeiraLinha(valorAbsoluto);
    this.cardDebitos.definirValorPrimeiraLinha(valorAbsoluto);
  }

  public possuiValor(ehCredito: boolean, valor: number): boolean {
    const form = ehCredito ? this.creditosForm : this.debitosForm;
    const rows = form.get('rows') as FormArray | null;
    if (!rows) return false;

    return rows.controls.some(row => {
      const rowValor = Number(row.get('valor')?.value);
      return !isNaN(rowValor) && Math.abs(rowValor - valor) < 0.005;
    });
  }

  public possuiValorDeTipoDaConta(ehCredito: boolean, valor: number, tipoLancamentoIdsValidos: Set<number>): boolean {
    const form = ehCredito ? this.creditosForm : this.debitosForm;
    const rows = form.get('rows') as FormArray | null;
    if (!rows) return false;

    return rows.controls.some(row => {
      const rowValor = Number(row.get('valor')?.value);
      const tipoId = row.get('tipoLancamento')?.value?.id;
      return !isNaN(rowValor) && Math.abs(rowValor - valor) < 0.005
        && tipoId != null && tipoLancamentoIdsValidos.has(tipoId);
    });
  }

  public buscar(nome: string, data: string): void {
    this.editarLoteLancamentosApiService.getLoteLancamentosNomeData(nome, data).subscribe((a: LoteLancamentos) => {
      a.data = new Date(a.data);
      this.form.get("nomeLote")!.setValue(a.nome);
      this.form.get("dataLote")!.setValue(a.data);
      this.setLancamentosFromForm(a);
      this.setLoteEdicao(a.id);
      this.loteEncontrado.emit(a);
    });
  }

  private setLoteEdicao(loteId: number): void {
    this.modoEdicao = true;
    this.loteId = loteId;
  }

  private getLancamentosFromForm(form: FormGroup, ehCredito: boolean): LancamentoDoLote[] {
    const lancamentos: LancamentoDoLote[] = [];

    const array = form.get('rows') as FormArray;
    array.controls.forEach((row) => {
      const valorAbsoluto = row.get('valor')?.value;

      const lancamento: LancamentoDoLote = {
        id: row.get('id')?.value ?? 0,
        valor: ehCredito ? valorAbsoluto : -valorAbsoluto,
        tipoLancamentoId: row.get('tipoLancamento')?.value?.id,
        observacao: row.get('observacoes')?.value
      };

      if (lancamento.valor != null && lancamento.tipoLancamentoId != null)
        lancamentos.push(lancamento);
    });

    return lancamentos;
  }

  private setLancamentosFromForm(lote: LoteLancamentos): void {
    const { creditos, debitos } = lote.lancamentos.reduce(
      (acc, l) => {
        if (l.valor >= 0) {
          acc.creditos.push(l);
        } else {
          acc.debitos.push({
            id: l.id,
            valor: -l.valor,
            tipoLancamentoId: l.tipoLancamentoId,
            observacao: l.observacao
          });
        }
        return acc;
      },
      { creditos: [] as LancamentoDoLote[], debitos: [] as LancamentoDoLote[] }
    );

    this.cardCreditos.addLancamentosToForm(creditos);
    this.cardDebitos.addLancamentosToForm(debitos);
  }

  public toggleTabDirecao(): void {
    this.tabCruzado = !this.tabCruzado;
  }

  public onTabFromLastRowCreditos(): void {
    this.cardDebitos.focusOrAddRowAndFocus();
  }

  public onTabFromLastRowDebitos(): void {
    this.cardCreditos.focusOrAddRowAndFocus();
  }

  public buscarClick(): void {
    if (!this.form.get("nomeLote")?.valid || !this.form.get("dataLote")?.valid) {
      this.messageService.showToastWarning("Preencher campos da busca.");
      this.form.markAllAsTouched();
      return;
    }

    if (this.form.dirty) {
      const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
        width: '467px',
        panelClass: 'no-padding-dialog',
        autoFocus: false
      });
      dialogRef.componentInstance!.titulo = "Buscar lote";
      dialogRef.componentInstance!.mensagem = `Após entrar no modo edição não será possivel reverter para o modo de criação.`;
      dialogRef.componentInstance!.mensagemBold = 'Deseja prosseguir?';
      dialogRef.componentInstance!.acaoConfirmacao = this.confirmaRealizarBusca.bind(this);
    }
  }

  private confirmaRealizarBusca(): void {
    const nome: string = this.form.get("nomeLote")!.value;
    const data: Date = this.form.get("dataLote")!.value;
    this.buscar(nome, data.toISOString());
  }

  public salvar(aoSalvar: () => void): void {
    if (this.form.invalid) {
      this.messageService.showToastWarning("Verificar preenchimento dos campos.");
      this.form.markAllAsTouched();
      return;
    }

    const lancamentos = this.getLancamentosFromForm(this.creditosForm, true)
      .concat(this.getLancamentosFromForm(this.debitosForm, false));

    const loteLancamentos: LoteLancamentos = {
      id: this.loteId,
      nome: this.form.get('nomeLote')?.value,
      data: this.form.get('dataLote')?.value as Date,
      lancamentos: lancamentos
    };

    this.editarLoteLancamentosApiService.salvarLoteLancamentos(loteLancamentos).subscribe(() => {
      this.messageService.showToastSuccess("Salvo com sucesso.");
      aoSalvar();
    });
  }

  public resetForm(): void {
    this.form.reset();
    this.modoEdicao = false;
    this.loteId = 0;
    this.cardCreditos.reset();
    this.cardDebitos.reset();
  }
}
